// Support Ticket Service (Phase 6 Skeleton)
// Central abstraction for performing support ticket operations.
// Goal: decouple UI components from raw franchisee API + event emission details.
// Non-breaking: current components remain visually identical; only creation path will migrate first.

import {
  beginMetadataEdit,
  ensureSession,
  gatherPendingMetadataUpdate,
  hasPersistedDraftEntryChanges,
  hasMetadataChanges as sessionHasMetadataChanges,
  updateDraftMetadata as sessionUpdateDraftMetadata,
  validateDraftMetadata as sessionValidateDraftMetadata
} from './support-ticket-session';
import { BranchQuoteSupportItemType, BranchQuoteSupportStatus } from '../../api/dealer-api-interface-franchisee';
import { buildSupportTicketCreateInput } from './support-editor-core';
import { containsFlag, flagEquals, sameText } from '../../../webmodule-common/other/ui/string-helper-functions';
import { FetchError } from '../../../scriptsLicenseServer/api/licenseserver-api';
import { getApiFactory } from '../../api/api-injector';

import { getSupportTypeDisplayValue } from './conversation-helper';

import { LangBranchQuoteSupportNew } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { sharedExecute } from '../../../webmodule-common/other/common/helpers/callbacks';
import { stagedExecution } from '../../../webmodule-common/other/general/event-source-client';
import { SupportTicketReasons } from './support-ticket-reasons';
import { WMEventSource } from '../../api/event-source';
import type {
  AddConversationAttachment,
  BranchQuoteSupport,
  InputAddBranchQuoteSupport,
  InputBoundConversationEntry,
  InputEditConversationEntry,
  InputUpdateBranchQuoteSupport,
  ResultBranchQuoteSupport,
  ResultGetBranchQuoteSupport,
  SupportQuoteItemLinkDetail,
  ViewBranchQuoteSupportInboxItem,
  ViewConversationEntry
} from '../../api/dealer-api-interface-franchisee';
import type { EventSourceHandler } from '../../../webmodule-common/other/general/event-source-client';
import type { SSEBranchQuoteSupport } from './sss-branch-quote-support';
import type { SupportTicketAnyReason } from './support-ticket-reasons';
import type { SupportTicketSession } from './support-ticket-session';

// NOTE (Phase: TicketListMigration): Introducing lightweight ticket list caching inside the service so that
// quote managers & UI components can source a single canonical list + reactive updates (SSE + local mutations)
// without each owning their own cache. This will be refined in later steps when event noise is reduced.

// Minimal context needed to create a support ticket (extracted from new ticket component)
export interface SupportTicketCreateContext {
  quoteId: string;
  supportType: BranchQuoteSupportItemType;
  subject: string;
  text: string;
  status?: BranchQuoteSupportStatus.Draft | BranchQuoteSupportStatus.New;
  attachments: AddConversationAttachment[];
  masterDocumentConversationId?: string | null;
  /** Optional single master document attachment seeded only for EngineeredToOrder tickets */
  masterDocumentFilename?: string | null;
  quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
  branchQuoteSupportId: string;
  conversationEntryId: string;
  draft?: boolean;
}

// Mutable context exposed to beforeCreate handlers allowing augmentation prior to persistence.
export interface MutableSupportTicketBeforeCreateContext extends SupportTicketCreateContext {
  /** Additional conversation entries to persist immediately after the initial entry (system notes etc.) */
  additionalEntries: {
    id: string;
    text: string;
    attachments: AddConversationAttachment[];
    users: string[];
    draft?: boolean;
  }[];
}

// Mutable context exposed to beforeEdit handlers for any ticket mutation.
export interface MutableSupportTicketBeforeEditContext {
  ticketId: string;
  /** Indicates if a status change is pending */
  hasStatusChange: boolean;
  /** Previous status (if status change is occurring) */
  previousStatus?: BranchQuoteSupportStatus | null;
  /** New status (if status change is occurring) */
  newStatus?: BranchQuoteSupportStatus | null;
}

// Context exposed to postEdit handlers after ticket mutation.
export interface SupportTicketPostEditContext {
  ticketId: string;
  ticket: BranchQuoteSupport;
  /** Indicates if a status change occurred */
  hasStatusChange: boolean;
  /** Previous status (if status change occurred) */
  previousStatus?: BranchQuoteSupportStatus | null;
  /** New status (if status change occurred) */
  newStatus?: BranchQuoteSupportStatus | null;
}

/**
 * SupportTicketService
 * Instance-scoped abstraction (will be owned by a Quote / QuoteManager) coordinating all support ticket mutations.
 * Responsibilities:
 *  - Convert UI intents into precise Franchisee API calls.
 *  - Emit canonical support:* events (via emitSupportEvent) with strongly typed payloads.
 *  - Centralize terminal status → closed event enrichment & diff classification.
 *  - Provide convenience orchestration helpers (ensureMasterDocument, applyFrameDiff) to remove multi-call duplication.
 * Non-responsibilities:
 *  - Caching / persistence beyond immediate API responses.
 *  - Business rule validation (delegated to higher-level manager before calling methods).
 */
export interface SupportTicketService {
  supplierName: string;
  /**
   * Transition ticket status. Emits: changed(kind='status'). If transition leads to Resolved/Cancelled emits ticket-closed with enrichment.
   */
  updateStatus: (args: UpdateStatusArgs) => Promise<boolean>;
  /** Current editing entry id for active ticket (null if creating new). */
  getEditingEntryId: (ticketId: string) => string | null;
  /** Ensure (or re-stage) a working draft conversation entry. forceUpdate re-wraps persisted draft even if unchanged. */
  ensureDraftConversationEntry: (ticketId: string, forceUpdate?: boolean) => string; // returns draft entry id
  /** Update draft conversation entry working copy (silent). */
  updateDraftConversationEntry: (
    ticketId: string,
    changes: Partial<{ text: string; attachments: AddConversationAttachment[]; users: string[] }>
  ) => void;
  /**
   * Load conversation entries.
   * - If forceReload true: full fetch.
   * - If fetchUpdatesOnly true and session already loaded: request only new entries since last recordVersion and merge.
   * Emits conversation-loaded after any mutation to session entries.
   */
  loadConversation: (ticketId: string) => Promise<ViewConversationEntry[]>; // first call full load, subsequent calls incremental
  // restageDraftConversationEntry removed; use ensureDraftConversationEntry(ticketId, true) instead.
  /** Stage (do not persist immediately) a new conversation entry to be combined on next unified save. */
  addPendingConversationEntry: (args: AddEntryArgs) => void;
  /** Obtain (and lazily create) the session object for a ticket. */
  getSession: (ticketId: string) => SupportTicketSession;
  /** Whether a session currently exists (cached) for a ticket id. */
  hasSession: (ticketId: string) => boolean;
  /** Dispose (remove) a cached session so that reopening starts clean (entries + draft state cleared). */
  disposeSession: (ticketId: string) => void;
  /** Convenience: session-scoped add entry (no need to pass ticket id twice). */
  /** Convenience: session-scoped update entry. */
  /** Convenience: set editing entry via session. */
  sessionSetEditingEntry: (session: SupportTicketSession, entryId: string | null) => void;
  /** Begin listening for SSE branch-quote-support events for a specific quote (id prefix filter) */
  attachSse: (options: AttachSseOptions) => void;
  /** Stop listening for SSE events (if previously attached) */
  detachSse: () => void;
  /** Begin draft metadata editing (idempotent). */
  beginMetadataEdit: (ticketId: string) => void;
  /** Push partial metadata edits into draft (creates draft if absent). */
  updateDraftMetadata: (
    ticketId: string,
    changes: Partial<{
      subject: string | null;
      quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
      masterDocumentFilename: string | null;
    }>
  ) => void;
  /** Whether there are unsaved metadata changes. */
  hasMetadataChanges: (ticketId: string) => boolean;
  /** Determine if there are unsaved changes for a specific ticket (draft or editing). */
  needsSave: (ticketId: string) => boolean;
  /** Register a fallback handler invoked ONLY if intrinsic needsSave(ticketId) returns false. */
  registerNeedsSave: (
    handler: (supportType: BranchQuoteSupportItemType | null, ticketId: string) => boolean | undefined
  ) => void;
  unregisterNeedsSave: (
    handler: (supportType: BranchQuoteSupportItemType | null, ticketId: string) => boolean | undefined
  ) => void;

  /** Validate current draft metadata (empty array = valid). */
  validateDraftMetadata: (ticketId: string) => string[];
  /** Persist changes for a ticket (draft creation or editing save). */
  saveTicket: (
    ticketId: string,
    options?: {
      finalizeDraftEntry?: boolean;
      combinedPublish?: boolean;
      status?: BranchQuoteSupportStatus | null;
      deleteDraftEntry?: boolean;
    }
  ) => Promise<boolean>;
  /** Begin a new ticket draft session. */
  createSupportTicketDraft: (ctx: {
    quoteId: string;
    supportType: BranchQuoteSupportItemType;
    supplierName?: string;
    branchQuoteSupportId?: string;
    conversationEntryId?: string;
    masterDocumentConversationId?: string | null;
    masterDocumentFilename?: string | null;
  }) => string;
  /** Update draft session fields. */
  updateDraftSession: (
    ticketId: string,
    changes: Partial<{
      subject: string;
      text: string;
      quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
      attachments: AddConversationAttachment[];
      masterDocumentFilename: string | null;
    }>
  ) => void;
  /** Validate current draft session (empty array => valid). */
  validateDraftSession: (ticketId: string) => string[];
  /** Quick support type lookup. */
  getSupportType: (ticketId: string) => BranchQuoteSupportItemType | null;
  /** Subscribe to immutable state snapshots. Returns unsubscribe function. Listener is invoked immediately with current state. */
  subscribe: (listener: SupportStateListener) => () => void;
  /** Get the current immutable state snapshot (do not mutate). */
  getState: () => SupportTicketsState;
  /** Hydrate a session with an already-fetched existing ticket (no conversation entries yet). */
  ensureTicketHydrated: (ticket: BranchQuoteSupport) => void;
  /** Register an async callback invoked AFTER a ticket is successfully created (persisted). */
  registerPostCreate: (handler: (ticket: BranchQuoteSupport, ctx: SupportTicketCreateContext) => Promise<void>) => void;
  /** Register an async callback invoked BEFORE a ticket is created allowing mutation of context prior to persistence. */
  registerBeforeCreate: (handler: (ctx: MutableSupportTicketBeforeCreateContext) => Promise<void>) => void;
  /** Register an async callback invoked AFTER any successful ticket mutation (status/metadata/conversation edit). */
  registerPostEdit: (handler: (ctx: SupportTicketPostEditContext) => Promise<void>) => void;
  /** Unified ticket open. When edit=true, stages metadata draft + draft conversation entry and emits composite publish. */
  openTicket: (
    ticketId: string,
    options?: { forceConversationReload?: boolean; edit?: boolean }
  ) => Promise<SupportTicketSession>;
  /** Register an async callback invoked BEFORE any ticket mutation to allow context augmentation (additional entries, metadata tweaks). */
  registerBeforeEdit: (handler: (ctx: MutableSupportTicketBeforeEditContext) => Promise<boolean>) => void;
  /** Unregister a previously registered post-edit callback. */
  unregisterPostEdit: (handler: (ctx: SupportTicketPostEditContext) => Promise<void>) => void;
  unregisterBeforeCreate: (handler: (ctx: MutableSupportTicketBeforeCreateContext) => Promise<void>) => void;
  unregisterBeforeEdit: (handler: (ctx: MutableSupportTicketBeforeEditContext) => Promise<boolean>) => void;
  /** Unregister a previously registered post-create callback. */
  unregisterPostCreate: (
    handler: (ticket: BranchQuoteSupport, ctx: SupportTicketCreateContext) => Promise<void>
  ) => void;
  /** Load (or force reload) the support ticket list for a quote. Returns cached or freshly fetched list. */
  loadTicketList: (options?: { force?: boolean }) => Promise<ResultGetBranchQuoteSupport | null>;
  /** Get the cached ticket list for a quote (null if never loaded). */
  getTicketList: () => ResultGetBranchQuoteSupport | null;
  /** Assign or change the active quote id for this service, flushing cached state. */
  setQuoteContext: (quoteSetId: string, quoteId: string) => void;
  /** Current quote id (null if not set). */
  getQuoteId: () => string | null;
  /** Create a shallow working clone that shares ticket list cache and optionally event listeners (publish bus). */
  clone: (options?: { shareEvents?: boolean; shareTicketList?: boolean }) => SupportTicketService;
  /** Dispose a clone instance (clears its local sessions, slots, SSE attachment). Does not affect shared ticket list or listeners. */
  disposeClone?: () => void;
  /**
   * Resolves all outstanding support tickets for the current quote set (backend batch) then creates a new QuoteReview ticket.
   * After completion publishes TicketListChanged so external consumers reload list content.
   * Returns the id of the newly created QuoteReview ticket or null if creation failed.
   */
  resolveAllAndCreateQuoteReviewTicket: (args: {
    quoteSetId: string;
    branchQuoteId: string;
    resolutionReason: string;
    subject: string;
  }) => Promise<string | null>;
  /**
   * Resolves all outstanding tickets for a quote set without creating a new QuoteReview ticket.
   * After completion publishes TicketListChanged so external consumers reload list content.
   */
  resolveAllTickets: (args: { quoteSetId: string; resolutionReason: string }) => Promise<boolean>;
  /**
   * Cancels a special item support ticket (e.g. after the underlying quote item is deleted) by
   * staging a cancellation note and issuing a status change to Cancelled. No-op if ticket already cancelled.
   * Returns true if a cancellation update was sent and persisted.
   */
  cancelSpecialItemSupportTicket: (args: { ticketId: string; reasonText: string }) => Promise<boolean>;
  reactivateSpecialItemSupportTicket: (args: { ticketId: string; reasonText: string }) => Promise<boolean>;
  /**
   * Relocates the latest QuoteReview support ticket to a new branch quote (used when creating an alternate supplier review quote).
   * No-op if no QuoteReview ticket exists or it already targets the provided branch quote id.
   * Returns the relocated ticket id on success, null on failure or no-op.
   */
  relocateQuoteReviewTicket: (newBranchQuoteId: string) => Promise<string | null>;
  /**
   * Updates the status (and optionally branchQuoteId relocation) of the latest QuoteReview ticket.
   * Returns the ticket id if updated, null if no QuoteReview exists or update failed.
   */
  updateQuoteReviewTicket: (args: {
    status: BranchQuoteSupportStatus;
    branchQuoteId?: string | null;
  }) => Promise<string | null>;
  /** Delete a draft support ticket. If ticket is persisted and still in Draft status calls API; else clears local draft session. Returns true on success or no-op, false on failure. */
  deleteTicket: (ticketId: string) => Promise<boolean>;
}

interface SupportTicketSharedContext {
  ticketLists: Map<string, ResultGetBranchQuoteSupport>;
  listeners: Set<SupportStateListener>;
}

class SupportTicketServiceImpl implements SupportTicketService {
  private quoteId: string | null = null;
  private quoteSetId: string | null = null;
  // slots removed – session.kind now identifies draft vs editing (map deleted in refactor)
  // Fallback needs-save handlers (ticketId-centric). Triggered only if intrinsic logic evaluates to false.
  private needsSaveHandlers: Set<
    (supportType: BranchQuoteSupportItemType | null, ticketId: string) => boolean | undefined
  > = new Set();
  supplierName: string;
  setQuoteContext(quoteSetId: string, quoteId: string): void {
    const changed = (this.quoteId && this.quoteId !== quoteId) || this.quoteSetId !== quoteSetId;
    if (changed) {
      this.quoteSetId = quoteSetId;
      this.quoteId = quoteId;
      // Flush ticket list + sessions (quote-scoped caches)
      this.ticketLists.clear();
      // dispose all sessions
      for (const id of Array.from(this.sessions.keys())) {
        this.disposeSession(id);
      }
      this.publish([], SupportTicketReasons.TicketListChanged);
    }
  }
  getQuoteId(): string | null {
    return this.quoteId;
  }
  // ---- Private Helpers (de-duplicated logic) ----
  /** Sort a ticket list newest-first by dateCreated (in-place). */
  private sortTicketListDesc(list: { items: BranchQuoteSupport[] } | undefined): void {
    if (!list) return;
    list.items.sort((a, b) => {
      const ad = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
      const bd = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
      return bd - ad; // DESC
    });
  }
  /** Map staged pending entries to InputBoundConversationEntry array. */
  // (mapPendingEntries removed after refactor: logic folded directly into saveAllChanges to avoid unused helper.)
  /** Publish either combined TicketChanged or granular reasons. */
  private publishChanges(ticketId: string, opts: { meta?: boolean; convo?: boolean; combined?: boolean }): void {
    if (opts.combined) {
      if (opts.meta || opts.convo) this.publish([ticketId], SupportTicketReasons.TicketChanged);
      return;
    }
    if (opts.meta) this.publish([ticketId], SupportTicketReasons.DraftStateChanged);
    if (opts.convo) this.publish([ticketId], SupportTicketReasons.ConversationChanged);
  }
  // Per-ticket sessions (ephemeral UI state)
  private sessions: Map<string, SupportTicketSession> = new Map();
  private sseAttached = false;
  private sseHandler?: EventSourceHandler;
  private sseQuoteId?: string;
  // activeTicketId & legacy global newTicketDraft removed; using per-session kind (draft|editing)
  // ---- Immutable snapshot + subscriptions ----
  private stateVersion = 0;
  private listeners: Set<SupportStateListener>;
  // Post-create side effect handlers (e.g., EngineeredToOrder quote item persistence driven by ticket lifecycle)
  private postCreateHandlers: Set<(ticket: BranchQuoteSupport, ctx: SupportTicketCreateContext) => Promise<void>> =
    new Set();
  // Post-edit side effect handlers (invoked after any successful mutation: conversation add/edit, metadata change, status transition)
  private postEditHandlers: Set<(ctx: SupportTicketPostEditContext) => Promise<void>> = new Set();
  private beforeCreateHandlers: Set<(ctx: MutableSupportTicketBeforeCreateContext) => Promise<void>> = new Set();
  private beforeEditHandlers: Set<(ctx: MutableSupportTicketBeforeEditContext) => Promise<boolean>> = new Set();
  private cachedState: SupportTicketsState | null = null;
  // ---- Ticket list cache (keyed by quoteId) ----
  private ticketLists: Map<string, ResultGetBranchQuoteSupport>;
  private sharedContext?: SupportTicketSharedContext;
  private isCloneInstance = false;

  constructor(shared?: { context?: SupportTicketSharedContext; shareEvents?: boolean; shareTicketList?: boolean }) {
    if (shared?.context && shared.shareTicketList) {
      this.sharedContext = shared.context;
      this.ticketLists = shared.context.ticketLists;
    } else {
      this.ticketLists = new Map();
    }
    if (shared?.context && shared.shareEvents) {
      this.listeners = shared.context.listeners;
      this.sharedContext = shared.context;
    } else {
      this.listeners = new Set();
    }
    if (shared) this.isCloneInstance = true;
  }
  // Track which quoteId is currently being fetched to avoid duplicate concurrent calls.
  private ticketListInflight: Set<string> = new Set();
  private publish(changedTicketIds: string[], reason?: SupportTicketAnyReason) {
    // Ensure draft correctness for all affected tickets BEFORE rebuilding immutable snapshot.
    // Rule: before any event publish of any kind we verify draft staging.
    for (const id of changedTicketIds) {
      if (this.sessions.has(id)) {
        try {
          // Non-force ensure: load pathways already force staging; here we just validate/correct collisions/new unsaved draft.
          this.ensureDraftConversationEntry(id);
        } catch (err) {
          this.trace('publish.ensureDraft.error', { id, err });
          throw err;
        }
      }
    }
    // Rebuild immutable state (structural sharing kept minimal for now)
    this.cachedState = this.buildState();
    const delta: SupportTicketsDelta = {
      version: this.cachedState.version,
      changedTicketIds,
      reason
    };
    // Fire listeners
    this.listeners.forEach(l => {
      try {
        l(this.cachedState!, delta);
      } catch (err) {
        this.trace('listener.error', err);
        throw err;
      }
    });
    // Evaluate needs-save transitions for all slot types after publish
    try {
      // Removed needsSave transition firing; fallback handlers only consulted in needsSave when intrinsic false.
    } catch (err) {
      this.trace('publish.needsSaveTransition.error', err);
      throw err;
    }
  }
  private buildState(): SupportTicketsState {
    const tickets: Record<string, SupportTicketSnapshot> = {};
    for (const [id, session] of this.sessions.entries()) {
      tickets[id] = {
        id,
        ticket: session.ticket || null,
        masterDocumentConversationId: session.masterDocumentConversationId,
        status: session.ticket?.status ?? null,
        recordVersion: session.recordVersion,
        conversationLoaded: session.loaded,
        entries: session.entries.slice(), // shallow copy for immutability
        draftMetadata: session.draftMeta
          ? {
              subject: session.draftMeta.subject,
              quoteItemLinks: { ...session.draftMeta.quoteItemLinks },
              masterDocumentFilename: session.draftMeta.masterDocumentFilename,
              touched: { ...session.draftMeta.touched },
              conflict: { ...session.draftMeta.conflict }
            }
          : null,
        hasMetadataChanges: sessionHasMetadataChanges(session)
      };
    }
    const ticketLists: SupportTicketsState['ticketLists'] = {};
    for (const [qid, list] of this.ticketLists.entries()) {
      ticketLists[qid] = {
        items: list.items.slice(),
        masterDocuments: (list.masterDocuments || []).slice(),
        conversationLinks: (list.conversationLinks || []).slice()
      };
    }
    // New snapshot draft representation: list of draft ticketIds derived from sessions
    const draftTicketIds: string[] = [];
    for (const [sid, session] of this.sessions.entries()) {
      if (session.kind === 'draft') draftTicketIds.push(sid);
    }
    return {
      version: ++this.stateVersion,
      draftTicketIds,
      tickets,
      ticketLists
    };
  }
  subscribe(listener: SupportStateListener): () => void {
    this.listeners.add(listener);
    // Lazy init state if first time
    if (!this.cachedState) this.cachedState = this.buildState();
    // Immediate invocation with current state (delta has no changes)
    try {
      listener(this.cachedState, {
        version: this.cachedState.version,
        changedTicketIds: [],
        reason: SupportTicketReasons.Init
      });
    } catch (err) {
      this.trace('listener.init.error', err);
      throw err;
    }
    return () => {
      this.listeners.delete(listener);
    };
  }
  /** Create a clone sharing ticket list + listeners based on options. Default: both shared. */
  clone(options?: { shareEvents?: boolean; shareTicketList?: boolean }): SupportTicketService {
    const shareEvents = options?.shareEvents !== false; // default true
    const shareTicketList = options?.shareTicketList !== false; // default true
    let context = this.sharedContext;
    const needShared = shareEvents || shareTicketList;
    if (needShared && !context) {
      context = { ticketLists: this.ticketLists, listeners: this.listeners };
      this.sharedContext = context;
    }
    const clone = new SupportTicketServiceImpl({ context, shareEvents, shareTicketList });
    // Carry quote context into clone so operations are scoped consistently
    if (this.quoteId && this.quoteSetId) clone.setQuoteContext(this.quoteSetId, this.quoteId);
    return clone;
  }
  disposeClone(): void {
    if (!this.isCloneInstance) return; // only meaningful for clones
    this.sessions.clear();
    // slots removed
    if (this.sseAttached && this.sseHandler) {
      try {
        WMEventSource.getInstance().removeEventListener([WMEventSource.branchQuoteSupport], this.sseHandler);
      } catch {
        /* ignore */
      }
      this.sseAttached = false;
      this.sseHandler = undefined;
    }
  }
  getState(): SupportTicketsState {
    if (!this.cachedState) this.cachedState = this.buildState();
    return this.cachedState;
  }
  ensureTicketHydrated(ticket: BranchQuoteSupport): void {
    // Create or retrieve session and populate core ticket fields if not already set or recordVersion changed
    const session = ensureSession(this.sessions, ticket.id);
    const needs = !session.ticket || session.ticket.recordVersion !== ticket.recordVersion;
    if (needs) {
      // Do not mark conversation loaded here; entries remain as-is
      session.applyTicket(ticket);
      this.publish([ticket.id], SupportTicketReasons.TicketChanged);
    }
  }
  private traceEnabled(): boolean {
    try {
      const v = localStorage.getItem('support.trace');
      if (v === null) return true; // default ON
      return !/^(false|0)$/i.test(v);
    } catch {
      return true;
    }
  }
  private trace(label: string, data?: any) {
    if (this.traceEnabled()) {
      // eslint-disable-next-line no-console
      console.debug(`[SupportService] ${label}`, data ?? '');
    }
  }
  /** Enforce non-null API contract; throws on null to unify error handling */
  private requireResult<T>(value: T | null, context: string): T {
    if (value === null) {
      throw new FetchError(500, `SupportService API returned null (${context})`);
    }
    return value;
  }
  // Removed legacy single-entry update helper (merged into unified saveTicket logic).
  /** Unified builder for InputUpdateBranchQuoteSupport to reduce duplication across mutation paths. */
  private buildUpdateInput(params: {
    branchQuoteSupportId: string;
    subject?: string | null;
    quoteItemLink?: Record<string, SupportQuoteItemLinkDetail> | null;
    status?: BranchQuoteSupportStatus | null;
    quoteItemIdToDetach?: string | null;
    newConversationEntries?: InputBoundConversationEntry[] | null;
    editConversationEntries?: InputEditConversationEntry[] | null;
    branchQuoteId?: string | null; // rarely used,
  }): InputUpdateBranchQuoteSupport {
    return {
      assignedSupplierUserId: null, // not supported in franchisee API. Auto Assign Logic will be used instead
      conversationId: null,
      notifySupplier: true,
      branchQuoteSupportId: params.branchQuoteSupportId,
      branchQuoteId: params.branchQuoteId ?? null,
      subject: params.subject !== undefined ? params.subject : null,
      quoteItemLink: params.quoteItemLink !== undefined ? params.quoteItemLink : null,
      status: params.status !== undefined ? params.status : null,
      quoteItemIdToDetach: params.quoteItemIdToDetach !== undefined ? params.quoteItemIdToDetach : null,
      newConversationEntries: params.newConversationEntries !== undefined ? params.newConversationEntries : null,
      editConversationEntries: params.editConversationEntries !== undefined ? params.editConversationEntries : null
    };
  }

  private execLoadConversation = sharedExecute(async ticketId => await this._execLoadConversation(ticketId));
  private async _execLoadConversation(
    ticketId: string
  ): Promise<{ entries: ViewConversationEntry[]; changed: boolean }> {
    const session = ensureSession(this.sessions, ticketId);
    const api = getApiFactory().franchisee();
    // After load we will restage draft state to ensure consistency.
    const firstLoad = !session.loaded || !session.recordVersion;
    try {
      const result = await api.getConversation({
        conversationId: ticketId,
        fetchUpdatesOnly: !firstLoad,
        maxRecordVersion: firstLoad ? null : session.recordVersion,
        sortAscending: true
      });
      const fetchedEntries = Array.isArray(result?.conversationEntries) ? result.conversationEntries : [];
      if (!firstLoad) {
        if (fetchedEntries.length === 0) return { entries: session.entries, changed: false };
        // Correct partial fetch merge semantics:
        // 1. Start from existing entries map keyed by id.
        // 2. For every fetched entry: replace the map value (override) or add if new.
        // 3. Keep existing entries not returned (server only sends deltas when incremental fetchUpdatesOnly=true).
        // 4. After merge, sort by dateCreated DESC (newest first) to satisfy UI ordering requirement.
        const byId: Map<string, ViewConversationEntry> = new Map();
        for (const existing of session.entries) byId.set(existing.id, existing);
        for (const incoming of fetchedEntries) byId.set(incoming.id, incoming); // override or add
        const merged = Array.from(byId.values()).sort((a, b) =>
          a.dateCreated === b.dateCreated ? 0 : a.dateCreated < b.dateCreated ? 1 : -1
        );
        const userIds = Array.from(new Set([...session.users, ...fetchedEntries.map(e => e.systemUserId)]));
        session.applyLoaded(merged, userIds);
      } else {
        // First load: just sort fetched entries descending by dateCreated
        const initial = fetchedEntries
          .slice()
          .sort((a, b) => (a.dateCreated === b.dateCreated ? 0 : a.dateCreated < b.dateCreated ? 1 : -1));
        const userIds = Array.from(new Set(initial.map(e => e.systemUserId)));
        session.applyLoaded(initial, userIds);
      }
      this.ensureDraftConversationEntry(ticketId, true);
      return { entries: session.entries, changed: true };
    } catch (err) {
      this.trace('execLoadConversation.fetch.error', { ticketId, firstLoad, err });
      throw err;
    }
  }
  private async execPostEdit(
    ticketId: string,
    statusChange?: { previous?: BranchQuoteSupportStatus | null; new?: BranchQuoteSupportStatus | null }
  ): Promise<void> {
    try {
      const t = ensureSession(this.sessions, ticketId).ticket;
      if (t) {
        const context: SupportTicketPostEditContext = {
          ticketId,
          ticket: t,
          hasStatusChange: !!(statusChange?.previous !== undefined || statusChange?.new !== undefined),
          previousStatus: statusChange?.previous,
          newStatus: statusChange?.new
        };
        for (const h of this.postEditHandlers) await h(context);
      }
    } catch (err) {
      this.trace('execPostEdit.error', err);
      throw err;
    }
  }

  private async execBeforeEdit(
    ticketId: string,
    statusChange?: { previous?: BranchQuoteSupportStatus | null; new?: BranchQuoteSupportStatus | null }
  ): Promise<boolean> {
    const context: MutableSupportTicketBeforeEditContext = {
      ticketId,
      hasStatusChange: !!(statusChange?.previous !== undefined || statusChange?.new !== undefined),
      previousStatus: statusChange?.previous,
      newStatus: statusChange?.new
    };
    let anyChanges = false;
    try {
      for (const h of this.beforeEditHandlers) {
        const handlerResult = await h(context);
        if (handlerResult) anyChanges = true;
      }
    } catch (err) {
      this.trace('execBeforeEdit.error', err);
      throw err;
    }
    return anyChanges;
  }
  /** @inheritdoc */
  /**
   * Internal low-level create (legacy wrapper chain removed). External callers now use createSupportTicketDraft + updateDraftSession + save.
   * Rationale: ensures a single unified path for event emission, ticket list insertion, conversation load and draft lifecycle clearing.
   */
  private async execCreateInternal(ctx: SupportTicketCreateContext): Promise<ResultBranchQuoteSupport | null> {
    // Mutable clone for before-create handlers
    const beforeCtx: MutableSupportTicketBeforeCreateContext = {
      ...ctx,
      additionalEntries: []
    };
    for (const h of this.beforeCreateHandlers) {
      try {
        await h(beforeCtx);
      } catch (err) {
        this.trace('create.beforeCreateHandler.error', err);
        throw err;
      }
    }
    const input: InputAddBranchQuoteSupport = buildSupportTicketCreateInput({
      quoteId: beforeCtx.quoteId,
      supportType: beforeCtx.supportType,
      subject: beforeCtx.subject,
      text: beforeCtx.text,
      status: beforeCtx.status,
      draft: !!beforeCtx.draft,
      attachments: beforeCtx.attachments,
      quoteItemLinks: beforeCtx.quoteItemLinks,
      branchQuoteSupportId: beforeCtx.branchQuoteSupportId,
      conversationEntryId: beforeCtx.conversationEntryId,
      masterDocumentConversationId: ctx.masterDocumentConversationId,
      masterDocumentFilename: beforeCtx.masterDocumentFilename
    });

    const api = getApiFactory().franchisee();
    const start = performance.now();
    this.trace('create.internal.start', { input });
    const result = await api.addBranchQuoteSupport(input);
    this.trace('create.internal.end', { ms: performance.now() - start, ok: !!result });

    if (result) {
      // Newly created ticket: initialize session with ticket object only; do NOT seed or select an editing entry.
      // Rationale: The initial brief (subject/text) has been persisted as the first conversation entry.
      // After creation the UI should present a clean editor (no pre-populated editing state) so user can add a NEW entry.
      const createdId = result.branchQuoteSupport.id;
      const session = ensureSession(this.sessions, createdId);
      // Master document filename: if provided in creation context (EngineeredToOrder) or echoed back via API master document entry.
      let mdFilename: string | undefined = beforeCtx.masterDocumentFilename || undefined;
      try {
        if (!mdFilename && result.branchQuoteSupport.masterDocumentConversationId) {
          const list = this.quoteId ? this.ticketLists.get(this.quoteId) : undefined;
          if (list) {
            const md = list.masterDocuments.find(m => m.id === result.branchQuoteSupport.masterDocumentConversationId);
            mdFilename = md?.attachments?.[0]?.filename;
          }
        }
      } catch (err) {
        this.trace('create.internal.masterDocFilename.resolve.error', err);
        throw err;
      }
      session.applyTicket(result.branchQuoteSupport, mdFilename);
      this.updateCachedTicketListItem(result.branchQuoteSupport, { allowInsert: true });
      // Load conversation immediately so editor can reflect initial persisted entry; stage draft after load.
      await this.execLoadConversation(createdId);

      // If we already have a ticket list cached for this quote, insert + sort (avoid refetch).
      try {
        const quoteId = result.branchQuoteSupport.branchQuoteId;
        if (quoteId && this.ticketLists.has(quoteId)) {
          const list = this.ticketLists.get(quoteId)!;
          // Avoid duplicate (forced id path)
          if (!list.items.some(i => i.id === createdId)) list.items.push(result.branchQuoteSupport);
          this.sortTicketListDesc(list);
        }
      } catch (err) {
        this.trace('create.ticketListAppend.error', err);
        throw err;
      }

      // Invoke post-create side effects (fire-and-await sequentially to ensure ordering; failures logged but do not reject ticket creation)
      for (const handler of this.postCreateHandlers) {
        try {
          await handler(result.branchQuoteSupport, ctx);
        } catch (sideErr) {
          this.trace('create.postCreateHandler.error', { sideErr });
          throw sideErr;
        }
      }

      // Do not applyLoaded optimistically; conversation component will invoke loadConversation() to fetch server entries.
      // Publishing orchestrated by unified save(ticketId) path (legacy draft wrapper removed).
    }
    return result ?? null;
  }
  registerPostCreate(handler: (ticket: BranchQuoteSupport, ctx: SupportTicketCreateContext) => Promise<void>): void {
    this.postCreateHandlers.add(handler);
  }
  registerBeforeCreate(handler: (ctx: MutableSupportTicketBeforeCreateContext) => Promise<void>): void {
    this.beforeCreateHandlers.add(handler);
  }
  unregisterBeforeCreate(handler: (ctx: MutableSupportTicketBeforeCreateContext) => Promise<void>): void {
    this.beforeCreateHandlers.delete(handler);
  }
  unregisterPostCreate(handler: (ticket: BranchQuoteSupport, ctx: SupportTicketCreateContext) => Promise<void>): void {
    this.postCreateHandlers.delete(handler);
  }

  /** @inheritdoc */
  addPendingConversationEntry(args: AddEntryArgs): void {
    // Stage a conversation entry to be persisted on next unified save.
    const ticketId = args.branchQuoteSupportId;
    if (!ticketId) return; // safety guard, though interface expects string
    const session = ensureSession(this.sessions, ticketId);
    // Replace existing staged entry if id already present (latest wins) rather than skipping.
    const mapped = {
      id: args.conversationEntryId,
      text: args.text,
      attachments: (args.attachments || []).map(a => ({ filename: a.filename })),
      users: (args.users || []).slice(),
      draft: !!args.draft
    };
    const existingIndex = session.pendingNewEntries.findIndex(e => e.id === args.conversationEntryId);
    if (existingIndex >= 0) {
      session.pendingNewEntries[existingIndex] = mapped;
      this.trace('addPendingConversationEntry.replaced', { ticketId, entryId: args.conversationEntryId });
    } else {
      session.pendingNewEntries.push(mapped);
      this.trace('addPendingConversationEntry.staged', { ticketId, count: session.pendingNewEntries.length });
    }
    // Do not publish yet; persistence + publish will occur during saveAllChanges()/save workflow.
  }
  ensureDraftConversationEntry(ticketId: string, forceUpdate?: boolean): string {
    const session = ensureSession(this.sessions, ticketId);
    session.ensureDraftEntry(() => newGuid(), forceUpdate);
    return session.draftEntry!.id;
  }
  updateDraftConversationEntry(
    ticketId: string,
    changes: Partial<{ text: string; attachments: AddConversationAttachment[]; users: string[] }>
  ): void {
    const session = ensureSession(this.sessions, ticketId);
    if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
    // Map AddConversationAttachment to minimal attachment shape for session draftEntry
    const mapped: any = { ...changes };
    if (changes.attachments) {
      mapped.attachments = changes.attachments.map(a => ({ filename: a.filename }));
    }
    session.updateDraftEntry(mapped);
  }
  /** @inheritdoc */
  async updateStatus(args: UpdateStatusArgs): Promise<boolean> {
    // Delegate to unified save with status override.
    const existing = ensureSession(this.sessions, args.branchQuoteSupportId);
    if (!existing) return false;
    return this.saveTicket(args.branchQuoteSupportId, {
      finalizeDraftEntry: true,
      combinedPublish: false,
      status: args.status
    });
  }

  /** @inheritdoc */
  getEditingEntryId(ticketId: string): string | null {
    const session = ensureSession(this.sessions, ticketId);
    return session.draftEntry ? session.draftEntry.id : null;
  }
  /** @inheritdoc */
  async loadConversation(ticketId: string): Promise<ViewConversationEntry[]> {
    const { entries, changed } = await this.execLoadConversation(ticketId);
    if (changed) this.publish([ticketId], SupportTicketReasons.ConversationChanged);
    return entries;
  }
  /** @inheritdoc */
  // restageDraftConversationEntry removed (use ensureDraftConversationEntry(ticketId, true)).
  registerPostEdit(handler: (ctx: SupportTicketPostEditContext) => Promise<void>): void {
    this.postEditHandlers.add(handler);
  }
  registerBeforeEdit(handler: (ctx: MutableSupportTicketBeforeEditContext) => Promise<boolean>): void {
    this.beforeEditHandlers.add(handler);
  }
  unregisterBeforeEdit(handler: (ctx: MutableSupportTicketBeforeEditContext) => Promise<boolean>): void {
    this.beforeEditHandlers.delete(handler);
  }
  unregisterPostEdit(handler: (ctx: SupportTicketPostEditContext) => Promise<void>): void {
    this.postEditHandlers.delete(handler);
  }
  /** @inheritdoc */
  getSession(ticketId: string): SupportTicketSession {
    const existed = this.sessions.has(ticketId);
    const session = ensureSession(this.sessions, ticketId);
    if (!existed) this.trace('session.created', { ticketId });
    return session;
  }
  /** @inheritdoc */
  async openTicket(
    ticketId: string,
    options?: { forceConversationReload?: boolean; edit?: boolean }
  ): Promise<SupportTicketSession> {
    // Ensure ticket list loaded prior to any open
    if (!this.getTicketList()) {
      await this.loadTicketList({ force: false });
    }
    const list = this.getTicketList();
    const ticket = list?.items.find(i => i.id === ticketId);
    if (!ticket) throw new Error('Ticket not found in cached list: ' + ticketId);
    // Ensure session
    const session = ensureSession(this.sessions, ticketId);
    const needsSnapshot = !session.ticket || session.ticket.recordVersion !== ticket.recordVersion;
    if (needsSnapshot) {
      let masterDocFilename: string | undefined;
      if (ticket.masterDocumentConversationId && list) {
        const md = list.masterDocuments.find(m => m.id === ticket.masterDocumentConversationId);
        masterDocFilename = md?.attachments?.[0]?.filename;
      }
      session.applyTicket(ticket, masterDocFilename);
    }
    const wasActive = false; // deprecated active concept
    const isEdit = !!options?.edit;
    let conversationChanged = false;
    const needLoad = options?.forceConversationReload || !session.loaded;
    if (needLoad) {
      const { changed } = await this.execLoadConversation(ticketId);
      conversationChanged = changed;
    } else {
      // Ensure draft wrapper exists when not loading
      try {
        this.ensureDraftConversationEntry(ticketId);
      } catch (err) {
        this.trace('openTicket.ensureDraft.error', err);
        throw err;
      }
    }
    if (isEdit) {
      // Stage metadata draft & ensure conversation draft entry
      try {
        beginMetadataEdit(session);
      } catch (err) {
        this.trace('openTicket.beginMetadataEdit.error', err);
        throw err;
      }
      try {
        session.ensureDraftEntry(() => newGuid(), false);
      } catch (err) {
        this.trace('openTicket.ensureDraftEntry.error', err);
        throw err;
      }
      // Composite publish ordering identical to prior openTicketForEditing
      const reasons: SupportTicketAnyReason[] = [];
      reasons.push(conversationChanged ? SupportTicketReasons.ConversationChanged : SupportTicketReasons.TicketChanged);
      reasons.push(SupportTicketReasons.DraftStateChanged);
      for (const r of reasons) this.publish([ticketId], r);
    } else {
      // View-only path: publish ticket changed if snapshot applied OR conversation loaded changed.
      if (needsSnapshot || conversationChanged || !wasActive) {
        const reasons: SupportTicketAnyReason[] = [];
        if (conversationChanged) reasons.push(SupportTicketReasons.ConversationChanged);
        else reasons.push(SupportTicketReasons.TicketChanged);
        for (const r of reasons) this.publish([ticketId], r);
      }
    }
    return session;
  }
  hasSession(ticketId: string): boolean {
    return this.sessions.has(ticketId);
  }
  disposeSession(ticketId: string): void {
    if (this.sessions.has(ticketId)) {
      this.sessions.delete(ticketId);
      this.trace('session.disposed', { ticketId });
    }
    this.publish([ticketId], SupportTicketReasons.TicketChanged); // session removal affects snapshots
  }
  /** @inheritdoc */
  sessionSetEditingEntry(session: SupportTicketSession, entryId: string | null): void {
    if (!entryId) return;
    if (session.draftEntry && session.draftEntry.id === entryId) return; // already staged
    const persistedDraft = session.entries.find(e => e.id === entryId && e.draft);
    if (persistedDraft) this.ensureDraftConversationEntry(session.id, true);
    this.publish([session.id], SupportTicketReasons.DraftStateChanged);
  }
  /** Attach SSE listener (idempotent). Only processes events whose item.branchQuoteId matches provided quoteId */
  attachSse(options: AttachSseOptions): void {
    if (this.sseAttached && this.sseQuoteId === options.quoteId) return;
    this.detachSse();
    this.sseQuoteId = options.quoteId;
    const exec = stagedExecution({
      event: async () => {
        /* no-op main batch action; we act per event */
      },
      threshold: 1500,
      cancelToken: undefined,
      eventTriggered: () => {
        /* optional visual signal outside scope */
      },
      eventActioned: async (data: SSEBranchQuoteSupport) => {
        const item = data.item;
        if (!item || item.branchQuoteId !== this.sseQuoteId) return; // quote scope filter
        // Remote deletion handling: if ticket status is Deleted remove from caches & publish list change.
        if (item.status === BranchQuoteSupportStatus.Deleted) {
          try {
            const list = this.quoteId ? this.ticketLists.get(this.quoteId) : undefined;
            if (list) {
              const beforeLen = list.items.length;
              list.items = list.items.filter(t => t.id !== item.id);
              if (beforeLen !== list.items.length) this.trace('sse.delete.list.removed', { ticketId: item.id });
            }
            if (this.sessions.has(item.id)) {
              // Remove session silently (avoid extra publish from disposeSession to preserve reason specificity)
              this.sessions.delete(item.id);
              this.trace('sse.delete.session.removed', { ticketId: item.id });
            }
          } catch (err) {
            this.trace('sse.delete.error', { ticketId: item.id, err });
            throw err;
          }
          // Publish ticket list change so UI components refresh; include removed id for delta awareness.
          this.publish([item.id], SupportTicketReasons.TicketListChanged);
          return; // skip normal update flow
        }
        const session = ensureSession(this.sessions, item.id);
        const prevVersion = session.recordVersion;
        // Fast path: if recordVersion matches we assume local state (editor) already has authoritative snapshot.
        if (prevVersion && prevVersion === item.recordVersion) {
          this.trace('sse.skip.nochange', { ticketId: item.id, v: item.recordVersion });
          return; // no publish, no list update, avoids race with in-flight local edits
        }
        session.applyTicket(item); // keep session snapshot fresh
        this.updateCachedTicketListItem(item); // merge into list cache
        // Merge master document entry if provided
        if (data.masterDocument) {
          try {
            const list = this.quoteId ? this.ticketLists.get(this.quoteId) : undefined;
            if (list) {
              const existingIndex = list.masterDocuments.findIndex(md => md.id === data.masterDocument!.id);
              if (existingIndex >= 0) {
                list.masterDocuments[existingIndex] = data.masterDocument!;
              } else {
                list.masterDocuments.push(data.masterDocument);
              }
            }
          } catch (err) {
            this.trace('sse.masterDocument.merge.error', err);
            throw err;
          }
        }
        // Merge quote item conversation link if provided
        if (data.link) {
          try {
            const list = this.quoteId ? this.ticketLists.get(this.quoteId) : undefined;
            if (list) {
              const existingLinkIndex = list.conversationLinks.findIndex(l => l.id === data.link!.id);
              if (existingLinkIndex >= 0) {
                // Shallow merge to preserve any local fields
                Object.assign(list.conversationLinks[existingLinkIndex], data.link);
              } else {
                list.conversationLinks.push(data.link);
              }
            }
          } catch (err) {
            this.trace('sse.link.merge.error', err);
            throw err;
          }
        }
        const isActive = this.sessions.has(item.id); // any hydrated session implies potential active editing
        const conversationLoaded = session.loaded;
        const isStale = !!prevVersion && prevVersion !== item.recordVersion;
        // Publish snapshot change (status/metadata) after applying
        this.publish([item.id], SupportTicketReasons.TicketChanged);
        if (isActive && conversationLoaded && isStale) {
          try {
            await this.execLoadConversation(item.id);
            this.publish([item.id], SupportTicketReasons.ConversationChanged);
          } catch (err) {
            this.trace('sse.reload.failed', err);
            throw err;
          }
        }
      }
    });
    this.sseHandler = exec;
    WMEventSource.getInstance().addEventListener([WMEventSource.branchQuoteSupport], exec);
    this.sseAttached = true;
  }
  detachSse(): void {
    if (!this.sseAttached || !this.sseHandler) return;
    try {
      WMEventSource.getInstance().removeEventListener([WMEventSource.branchQuoteSupport], this.sseHandler);
    } catch {
      /* ignore */
    }
    this.sseAttached = false;
    this.sseHandler = undefined;
    this.sseQuoteId = undefined;
  }
  getSupportType(ticketId: string): BranchQuoteSupportItemType | null {
    const session = this.sessions.get(ticketId);
    return session?.supportType ?? null;
  }
  beginMetadataEdit(ticketId: string): void {
    const session = ensureSession(this.sessions, ticketId);
    beginMetadataEdit(session);
    this.publish([ticketId], SupportTicketReasons.DraftStateChanged);
  }
  updateDraftMetadata(
    ticketId: string,
    changes: Partial<{
      subject: string | null;
      quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
      masterDocumentFilename: string | null;
    }>
  ): void {
    const session = ensureSession(this.sessions, ticketId);
    sessionUpdateDraftMetadata(session, changes);
    // Intentionally no publish: keystroke-level draft changes should not broadcast or trigger re-render.
  }
  hasMetadataChanges(ticketId: string): boolean {
    return sessionHasMetadataChanges(ensureSession(this.sessions, ticketId));
  }

  needsSave(ticketId: string): boolean {
    const session = this.sessions.get(ticketId);
    if (!session) return false;
    if (session.kind === 'draft') {
      const subject = (session.draftMeta?.subject ?? '').trim();
      const text = (session.draftEntry?.text ?? '').trim();
      const attachCount = session.draftEntry?.attachments?.length ?? 0;
      const intrinsic = subject.length > 0 || text.length > 0 || attachCount > 0;
      if (intrinsic) return true;
      // Consult fallback handlers only when intrinsic false
      for (const h of this.needsSaveHandlers) {
        try {
          const r = h(session.supportType ?? null, ticketId);
          if (r === true) return true;
          // r === false or undefined => continue checking others
        } catch (err) {
          this.trace('needsSave.handler.error', err);
          throw err;
        }
      }
      return false;
    }
    if (session.kind === 'editing') {
      if (gatherPendingMetadataUpdate(session)) return true;
      const draft = session.draftEntry;
      if (draft) {
        const hasContent =
          !!(draft.text && draft.text.trim().length > 0) ||
          (draft.attachments && draft.attachments.length > 0) ||
          (draft.users && draft.users.length > 0);
        const hasDraftChanges = draft.existingPersisted ? hasPersistedDraftEntryChanges(session) : hasContent;
        if (hasDraftChanges) return true;
      }
      if (session.pendingNewEntries.length > 0) return true;
      const masterDocId = session.masterDocumentConversationId;
      const masterDocUpdated =
        masterDocId &&
        session.draftMeta?.masterDocumentFilename &&
        session.baselineMeta?.masterDocumentFilename !== session.draftMeta?.masterDocumentFilename;
      if (masterDocUpdated) return true;
      // Intrinsic editing false -> consult fallbacks
      for (const h of this.needsSaveHandlers) {
        try {
          const r = h(session.supportType ?? null, ticketId);
          if (r === true) return true;
        } catch (err) {
          this.trace('needsSave.handler.error', err);
          throw err;
        }
      }
      return false;
    }
    return false;
  }
  validateDraftMetadata(ticketId: string): string[] {
    return sessionValidateDraftMetadata(ensureSession(this.sessions, ticketId));
  }

  /** Unified save for a given support type slot (draft or editing).
   * draft mode: persists as draft or finalizes first entry based on finalizeDraftEntry.
   * editing mode: applies metadata changes, draft updates, new entries, deletions, status changes.
   * deleteDraftEntry: when true in editing mode, deletes the current persisted draft entry (or discards unsaved draft) instead of updating/finalizing.
   */
  async saveTicket(
    ticketId: string,
    options?: {
      finalizeDraftEntry?: boolean;
      combinedPublish?: boolean;
      status?: BranchQuoteSupportStatus;
      deleteDraftEntry?: boolean;
    }
  ): Promise<boolean> {
    const sessionExisting = this.sessions.get(ticketId);
    if (!sessionExisting) return true;
    if (sessionExisting.kind === 'draft') {
      const creationStatus: BranchQuoteSupportStatus.Draft | BranchQuoteSupportStatus.New | undefined = flagEquals(
        options?.status ?? BranchQuoteSupportStatus.Draft,
        BranchQuoteSupportStatus.Draft,
        BranchQuoteSupportStatus.New
      )
        ? (options?.status as BranchQuoteSupportStatus.Draft | BranchQuoteSupportStatus.New | undefined)
        : BranchQuoteSupportStatus.Draft;
      const errs = this.validateDraftSession(ticketId);
      if (errs.length) return true;
      const draftEntry = sessionExisting.draftEntry;
      const subject = sessionExisting.draftMeta?.subject ?? '';
      const quoteItemLinks = sessionExisting.draftMeta?.quoteItemLinks ?? {};
      const created = await this.execCreateInternal({
        quoteId: this.quoteId!,
        supportType: sessionExisting.supportType!,
        subject,
        status: creationStatus,
        text: draftEntry?.text ?? '',
        draft: !options?.finalizeDraftEntry,
        attachments: (draftEntry?.attachments || []).map(a => ({ filename: a.filename })),
        quoteItemLinks: quoteItemLinks,
        branchQuoteSupportId: ticketId,
        conversationEntryId: draftEntry?.id ?? newGuid(),
        masterDocumentConversationId: sessionExisting.masterDocumentConversationId ?? null,
        masterDocumentFilename: sessionExisting.draftMeta?.masterDocumentFilename ?? null
      });
      if (!created) return false;
      sessionExisting.kind = 'editing';
      await this.openTicket(ticketId, { forceConversationReload: false, edit: true });
      this.publish([ticketId], SupportTicketReasons.NewTicketWorkflow);
      return true;
    }
    if (sessionExisting.kind === 'editing') {
      const activeId = ticketId;
      const session = ensureSession(this.sessions, activeId);
      const finalizedDraftEntry = !!options?.finalizeDraftEntry && !options?.deleteDraftEntry;
      const combinedPublish = !!options?.combinedPublish;
      const statusOverride = options?.status ?? null;
      const deleteDraftEntry = !!options?.deleteDraftEntry;
      this.trace('save.start', { ticketId, finalizedDraftEntry, combinedPublish });
      const currentStatus = session.ticket?.status ?? null;
      const statusChange = statusOverride !== null ? { previous: currentStatus, new: statusOverride } : undefined;
      const beforeEditRequiresSave = (await this.execBeforeEdit(activeId, statusChange)) || this.needsSave(activeId);
      const pendingMeta = gatherPendingMetadataUpdate(session);
      const draft = session.draftEntry;
      const pendingStaged = session.pendingNewEntries;
      let needDraftPersist = false;
      if (draft) {
        const hasContent =
          !!(draft.text && draft.text.trim().length > 0) ||
          (draft.attachments && draft.attachments.length > 0) ||
          (draft.users && draft.users.length > 0);
        needDraftPersist = draft.existingPersisted
          ? !!options?.deleteDraftEntry || hasPersistedDraftEntryChanges(session) || finalizedDraftEntry
          : hasContent;
      }
      const masterDocId = session.masterDocumentConversationId;
      const masterDocUpdated =
        masterDocId &&
        session.draftMeta?.masterDocumentFilename &&
        session.baselineMeta?.masterDocumentFilename !== session.draftMeta?.masterDocumentFilename;
      const hasPendingStaged = (pendingStaged && pendingStaged.length > 0) || !!masterDocUpdated;
      if (!pendingMeta && !needDraftPersist && !hasPendingStaged && !statusOverride && !beforeEditRequiresSave)
        return true;
      const api = getApiFactory().franchisee();
      let newConversationEntries: InputBoundConversationEntry[] | null = null;
      let editConversationEntries: InputEditConversationEntry[] | null = null;
      if (draft) {
        // delete path takes precedence over finalize/update
        if (deleteDraftEntry) {
          if (draft.existingPersisted) {
            editConversationEntries = [
              {
                id: draft.id,
                text: draft.text,
                users: draft.users,
                draft: false,
                attachments: [],
                deleteEntry: true,
                forceUpdate: !!statusOverride
              }
            ];
          } else {
            // Unsaved draft -> just discard locally; no server call needed for draft entry itself
            session.draftEntry = null;
            session.baselineDraftEntry = null;
          }
        } else if (needDraftPersist) {
          if (draft.existingPersisted) {
            editConversationEntries = [
              {
                id: draft.id,
                text: draft.text,
                users: draft.users,
                draft: !options?.finalizeDraftEntry,
                attachments: draft.attachments.slice(),
                deleteEntry: false,
                forceUpdate: !!statusOverride
              }
            ];
          } else {
            newConversationEntries = [
              {
                id: draft.id,
                text: draft.text,
                users: draft.users,
                draft: !options?.finalizeDraftEntry,
                attachments: draft.attachments.slice()
              }
            ];
          }
        }
      }
      // add new conversation entries
      if (pendingStaged && pendingStaged.length > 0) {
        if (!newConversationEntries) newConversationEntries = [];
        for (const pe of pendingStaged) {
          newConversationEntries.push({
            id: pe.id,
            text: pe.text,
            users: pe.users,
            draft: !!pe.draft,
            attachments: []
          });
        }
      }
      // NOTE: Do NOT prune deleteEntry edits before API call; backend must process them.
      // Master document change detection & persistence (filename updated OR removed)
      if (masterDocId) {
        const beforeFile = session.baselineMeta?.masterDocumentFilename ?? null;
        const afterFile = session.draftMeta?.masterDocumentFilename ?? null;
        const changed = !sameText(beforeFile || undefined, afterFile || undefined); // case-insensitive diff
        if (changed) {
          if (!editConversationEntries) editConversationEntries = [];
          editConversationEntries.push({
            id: masterDocId,
            text: '',
            users: [],
            draft: false,
            attachments: afterFile ? [{ filename: afterFile }] : [],
            deleteEntry: false,
            forceUpdate: true
          });
        }
      }

      // NOTE: masterDocumentFilename not yet part of InputUpdateBranchQuoteSupport; persistence handled via creation path only.
      // If backend adds field, re-enable mapping here.
      const input = this.buildUpdateInput({
        branchQuoteSupportId: activeId,
        subject: pendingMeta?.subject ?? null,
        quoteItemLink: pendingMeta?.quoteItemLink ?? null,
        status: statusOverride,
        newConversationEntries,
        editConversationEntries
      });
      let ok = false;
      try {
        const raw = await api.updateBranchQuoteSupport(input);
        if (raw) {
          const result = this.requireResult(raw, 'save');
          if (result) {
            // Clear draft metadata FIRST so applyTicket will refresh baseline with fresh data
            if (pendingMeta && !pendingMeta.hasConflicts) {
              session.draftMeta = null;
            }
            session.applyTicket(
              result.branchQuoteSupport,
              result.masterDocument?.attachments[0]?.filename ?? undefined
            );
            if (pendingMeta) {
              if (pendingMeta.hasConflicts && session.draftMeta) {
                if (pendingMeta.subject && !session.draftMeta.conflict.subject)
                  session.draftMeta.touched.subject = false;
                if (pendingMeta.quoteItemLink && !session.draftMeta.conflict.links)
                  session.draftMeta.touched.links = false;
                if (pendingMeta.masterDocumentFilename && !session.draftMeta.conflict.masterDoc)
                  session.draftMeta.touched.masterDoc = false;
              }
            }
            const deletionIds = (editConversationEntries || []).filter(e => e.deleteEntry).map(e => e.id);
            const hasDeletes = deletionIds.length > 0;
            const needConversationPublish = needDraftPersist || hasPendingStaged || hasDeletes || !!statusOverride;
            if (draft && (deleteDraftEntry || needDraftPersist || statusOverride)) {
              if (options?.finalizeDraftEntry) {
                session.draftEntry = null;
                session.baselineDraftEntry = null; // clear persisted draft baseline when finalized
              } else if (deleteDraftEntry) {
                session.draftEntry = null;
                session.baselineDraftEntry = null;
              } else {
                draft.existingPersisted = true;
                draft.touched = {};
              }
            }
            if (hasPendingStaged) session.pendingNewEntries = [];
            if (hasDeletes) {
              // Remove deleted entries locally before reload to avoid UI flicker
              session.entries = session.entries.filter(e => !deletionIds.includes(e.id));
              // If current draft entry was deleted, drop it so a fresh one can be staged after reload
              if (session.draftEntry && deletionIds.includes(session.draftEntry.id)) {
                session.draftEntry = null;
                session.baselineDraftEntry = null;
              }
            }
            if (needConversationPublish) {
              try {
                await this.execLoadConversation(activeId);
                // Always ensure a draft entry after reload (especially if previous draft was deleted)
                this.ensureDraftConversationEntry(activeId, true);
              } catch (err) {
                this.trace('save.reloadConversation.failed', err);
                throw err;
              }
            }
            this.updateCachedTicketListItem(result.branchQuoteSupport);
            // Merge master document entry (if any) via helper for tidy reuse
            this.mergeMasterDocumentIntoCache(
              result.masterDocument! || null,
              result.branchQuoteSupport.branchQuoteId || null
            );
            // Re-stage metadata for continued editing after ALL cache updates are complete
            // This ensures beginMetadataEdit uses the freshly updated baseline data including master document
            if (pendingMeta && !pendingMeta.hasConflicts) {
              beginMetadataEdit(session);
            }
            // Invalidate snapshot BEFORE post-edit handlers.
            // Any getState() inside handlers will lazily rebuild with the latest session mutations at first access
            // (avoids premature version bump and ensures snapshot reflects state as close as possible to handler timing).
            this.cachedState = null;
            await this.execPostEdit(activeId, statusChange);
            // Publish AFTER cache + master document merge + post edit hooks to ensure subscribers see fully updated state.
            const statusIncluded = !!statusOverride;
            if (combinedPublish || statusIncluded) {
              if (pendingMeta || needConversationPublish || statusIncluded) {
                this.publish([activeId], SupportTicketReasons.TicketChanged);
              }
            } else {
              this.publishChanges(activeId, {
                meta: !!pendingMeta || statusIncluded,
                convo: needConversationPublish,
                combined: combinedPublish
              });
            }
            ok = true;
          }
        }
      } catch (err) {
        this.trace('save.error', err);
        throw err;
      }
      this.trace('save.end', { ok });
      return ok;
    }
    return true;
  }
  // ---- New Ticket Draft Management ----
  createSupportTicketDraft(ctx: {
    quoteId: string;
    supportType: BranchQuoteSupportItemType;
    supplierName?: string;
    branchQuoteSupportId?: string;
    conversationEntryId?: string;
    masterDocumentConversationId?: string | null;
    masterDocumentFilename?: string | null;
  }): string {
    if (!this.getTicketList()) {
      this.loadTicketList({ force: false }).catch(err => {
        this.trace('createSupportTicketDraft.autoload.error', err);
        throw err;
      });
    }
    const forcedTicketId = ctx.branchQuoteSupportId?.trim();
    const forcedEntryId = ctx.conversationEntryId?.trim();
    const ticketId = forcedTicketId?.length ? forcedTicketId : newGuid();
    const entryId = forcedEntryId?.length ? forcedEntryId : newGuid();
    const session = ensureSession(this.sessions, ticketId, 'draft', ctx.supportType);
    session.supplierName = ctx.supplierName ?? '';
    session.baselineMeta = null;
    session.draftMeta = {
      subject: '',
      quoteItemLinks: {},
      masterDocumentFilename: ctx.masterDocumentFilename ?? null,
      touched: {},
      conflict: {}
    };
    session.draftEntry = {
      id: entryId,
      text: '',
      attachments: [],
      users: [],
      existingPersisted: false,
      draftFlag: true,
      touched: {}
    };
    session.masterDocumentConversationId = ctx.masterDocumentConversationId ?? null;
    this.trace('draft.session.begin', { ticketId, supportType: ctx.supportType, quoteId: ctx.quoteId });
    this.publish([], SupportTicketReasons.NewTicketWorkflow);
    return ticketId;
  }
  updateDraftSession(
    ticketId: string,
    changes: Partial<{
      subject: string;
      text: string;
      quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
      attachments: AddConversationAttachment[];
      masterDocumentFilename: string | null;
    }>
  ): void {
    const session = this.sessions.get(ticketId);
    if (!session || session.kind !== 'draft') return;
    if (session.draftMeta) {
      if (Object.prototype.hasOwnProperty.call(changes, 'subject') && typeof changes.subject === 'string') {
        session.draftMeta.subject = changes.subject;
        session.draftMeta.touched.subject = true;
      }
      if (changes.quoteItemLinks) {
        session.draftMeta.quoteItemLinks = { ...changes.quoteItemLinks };
        session.draftMeta.touched.links = true;
      }
      if (Object.prototype.hasOwnProperty.call(changes, 'masterDocumentFilename')) {
        session.draftMeta.masterDocumentFilename = changes.masterDocumentFilename ?? null;
        session.draftMeta.touched.masterDoc = true;
      }
    }
    if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
    if (session.draftEntry) {
      if (Object.prototype.hasOwnProperty.call(changes, 'text') && typeof changes.text === 'string') {
        session.draftEntry.text = changes.text;
        session.draftEntry.touched.text = true;
      }
      if (changes.attachments) {
        session.draftEntry.attachments = changes.attachments.map(a => ({ filename: a.filename }));
        session.draftEntry.touched.attachments = true;
      }
    }
  }
  validateDraftSession(ticketId: string): string[] {
    const session = this.sessions.get(ticketId);
    if (!session || session.kind !== 'draft') return [];
    const errors: string[] = [];
    const subj = (session.draftMeta?.subject ?? '').trim();
    if (!subj) {
      const st = session.supportType ?? BranchQuoteSupportItemType.All;
      errors.push(LangBranchQuoteSupportNew.pleaseAddSupportSubject({ supportType: getSupportTypeDisplayValue(st) }));
    }
    const txt = (session.draftEntry?.text ?? '').trim();
    if (!txt) {
      const st = session.supportType ?? BranchQuoteSupportItemType.All;
      errors.push(
        LangBranchQuoteSupportNew.pleaseProvideSupportInitialTopic({
          supportType: getSupportTypeDisplayValue(st),
          supplierName: session.supplierName || this.supplierName
        })
      );
    }
    return errors;
  }
  registerNeedsSave(
    handler: (supportType: BranchQuoteSupportItemType | null, ticketId: string) => boolean | undefined
  ): void {
    this.needsSaveHandlers.add(handler);
  }
  unregisterNeedsSave(
    handler: (supportType: BranchQuoteSupportItemType | null, ticketId: string) => boolean | undefined
  ): void {
    this.needsSaveHandlers.delete(handler);
  }
  // Replaced by type-specific APIs above.
  // legacy draft helpers removed (createSupportTicketDraft/updateDraftSession/validateDraftSession/save now handle lifecycle)
  // ---- Ticket List Cache Public API ----
  async loadTicketList(options?: { force?: boolean }): Promise<ResultGetBranchQuoteSupport | null> {
    const quoteId = this.quoteId;
    const quoteSetId = this.quoteSetId;
    const force = options?.force;
    if (!quoteId || !quoteSetId) return null;
    if (!force && this.ticketLists.has(quoteId)) return this.ticketLists.get(quoteId)!;
    if (this.ticketListInflight.has(quoteId)) {
      // Simple spin-wait via microtask queue until inflight completes (lightweight, UI already shows spinner elsewhere)
      await new Promise(r => setTimeout(r, 25));
      return this.ticketLists.get(quoteId) ?? null;
    }
    this.ticketListInflight.add(quoteId);
    try {
      const api = getApiFactory().franchisee();
      const result = await api.getBranchQuoteSupport({
        quoteSetId: quoteSetId,
        branchQuoteSupportId: null,
        quoteId: quoteId
      });
      if (result) {
        const existing = this.ticketLists.get(quoteId);
        this.ticketLists.set(quoteId, result);
        // Publish only if first load or item count differs (list structural change)
        if (!existing || existing.items.length !== result.items.length) {
          const ids = result.items.map(i => i.id);
          this.publish(ids, SupportTicketReasons.TicketListChanged);
        }
      }
      return result ?? null;
    } catch (err) {
      this.trace('loadTicketList.error', { quoteId, err });
      throw err;
    } finally {
      this.ticketListInflight.delete(quoteId);
    }
  }
  getTicketList(): ResultGetBranchQuoteSupport | null {
    if (!this.quoteId) return null;
    return this.ticketLists.get(this.quoteId) ?? null;
  }

  // ---- Internal helper to merge a single ticket into an existing cached list ----
  private updateCachedTicketListItem(ticket: BranchQuoteSupport, opts?: { allowInsert?: boolean }): void {
    try {
      const quoteId = ticket.branchQuoteId;
      if (!quoteId) return;
      const list = this.ticketLists.get(quoteId);
      if (!list) {
        if (opts?.allowInsert) {
          this.ticketLists.set(quoteId, { conversationLinks: [], items: [ticket], masterDocuments: [] });
        }
        return;
      }
      const existing = list.items.find(i => i.id === ticket.id);
      if (existing) {
        Object.assign(existing, ticket);
      } else if (opts?.allowInsert) {
        list.items.push(ticket);
        // Always keep items sorted by dateCreated DESC (newest first)
        list.items.sort((a, b) => {
          const ad = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const bd = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return bd - ad;
        });
      }
    } catch (err) {
      this.trace('updateCachedTicketListItem.error', err);
      throw err;
    }
  }

  /** Merge or append master document conversation entry into cached list (used in SSE & saveTicket paths). */
  private mergeMasterDocumentIntoCache(masterDocument: ViewConversationEntry | null, quoteId: string | null): void {
    if (!masterDocument || !quoteId) return;
    try {
      const list = this.ticketLists.get(quoteId);
      if (!list) return;
      const mdIdx = list.masterDocuments.findIndex(md => md.id === masterDocument.id);
      if (mdIdx >= 0) {
        list.masterDocuments[mdIdx] = masterDocument;
      } else {
        list.masterDocuments.push(masterDocument);
      }
    } catch (err) {
      this.trace('mergeMasterDocumentIntoCache.error', err);
      throw err;
    }
  }

  async resolveAllAndCreateQuoteReviewTicket(args: {
    quoteSetId: string;
    branchQuoteId: string;
    resolutionReason: string;
    subject: string;
  }): Promise<string | null> {
    try {
      const api = getApiFactory().franchisee();
      // Resolve all outstanding tickets for the quote set
      await api.resolveBranchQuoteSupport({
        quoteSetId: args.quoteSetId,
        resolutionReason: args.resolutionReason
      });
      // Create new QuoteReview support ticket
      const id = newGuid();
      const addResult = await api.addBranchQuoteSupport({
        quoteItemLink: null,
        id,
        masterDocumentConversationEntry: null,
        conversationId: id,
        subject: args.subject,
        branchQuoteId: args.branchQuoteId,
        conversationEntry: null,
        status: BranchQuoteSupportStatus.New,
        type: BranchQuoteSupportItemType.QuoteReview
      });
      // Flush cached list for current quote and publish change so consumers reload
      if (this.quoteId) this.ticketLists.delete(this.quoteId);
      this.publish([], SupportTicketReasons.TicketListChanged);
      return addResult?.branchQuoteSupport?.id ?? null;
    } catch (err) {
      this.trace('resolveAllAndCreateQuoteReviewTicket.error', err);
      throw err;
    }
  }

  async resolveAllTickets(args: { quoteSetId: string; resolutionReason: string }): Promise<boolean> {
    try {
      const api = getApiFactory().franchisee();
      // Resolve all outstanding tickets for the quote set
      await api.resolveBranchQuoteSupport({
        quoteSetId: args.quoteSetId,
        resolutionReason: args.resolutionReason
      });
      // Flush cached list for current quote and publish change so consumers reload
      if (this.quoteId) this.ticketLists.delete(this.quoteId);
      this.publish([], SupportTicketReasons.TicketListChanged);
      return true;
    } catch (err) {
      this.trace('resolveAllTickets.error', err);
      throw err;
    }
  }

  async cancelSpecialItemSupportTicket(args: { ticketId: string; reasonText: string }): Promise<boolean> {
    const { ticketId, reasonText } = args;
    return this._performSpecialItemStatusTransition({
      mode: 'cancel',
      ticketId,
      noteText: reasonText
    });
  }

  async reactivateSpecialItemSupportTicket(args: { ticketId: string; reasonText: string }): Promise<boolean> {
    const { ticketId, reasonText } = args;
    return this._performSpecialItemStatusTransition({
      mode: 'reactivate',
      ticketId,
      noteText: reasonText
    });
  }

  async relocateQuoteReviewTicket(newBranchQuoteId: string): Promise<string | null> {
    try {
      if (!newBranchQuoteId) return null;
      // Load ticket list to identify latest QuoteReview ticket.
      const list = this.getTicketList();
      if (!list) return null;
      const quoteReviewTickets = list.items.filter(t => t.type === BranchQuoteSupportItemType.QuoteReview);
      if (!quoteReviewTickets.length) return null;
      // Pick latest by dateCreated (list may already be sorted but enforce)
      quoteReviewTickets.sort((a, b) => b.dateCreated.localeCompare(a.dateCreated));
      const latest = quoteReviewTickets[0];
      if (latest.branchQuoteId === newBranchQuoteId) return latest.id; // already relocated
      const api = getApiFactory().franchisee();

      const update = await api.updateBranchQuoteSupport({
        conversationId: null,
        assignedSupplierUserId: null,
        notifySupplier: true,
        quoteItemLink: null,
        branchQuoteSupportId: latest.id,
        quoteItemIdToDetach: null,
        status: null,
        branchQuoteId: newBranchQuoteId,
        subject: null,
        newConversationEntries: null,
        editConversationEntries: null
      });
      if (update) {
        // Invalidate local cached list so next access reflects relocation.
        if (this.quoteId) this.ticketLists.delete(this.quoteId);
        this.publish([latest.id], SupportTicketReasons.TicketChanged);
        return latest.id;
      }
      return null;
    } catch (err) {
      this.trace('relocateQuoteReviewTicket.error', err);
      throw err;
    }
  }

  async updateQuoteReviewTicket(args: {
    status: BranchQuoteSupportStatus;
    branchQuoteId?: string | null;
  }): Promise<string | null> {
    try {
      const list = this.getTicketList();
      if (!list) return null;
      const quoteReviewTickets = list.items.filter(t => t.type === BranchQuoteSupportItemType.QuoteReview);
      if (!quoteReviewTickets.length) return null;
      // Latest by date
      quoteReviewTickets.sort((a, b) => b.dateCreated.localeCompare(a.dateCreated));
      const latest = quoteReviewTickets[0];
      const api = getApiFactory().franchisee();
      const branchQuoteId = args.branchQuoteId ?? null;
      // If neither status nor branch quote id change, skip
      const relocateNeeded = branchQuoteId && latest.branchQuoteId !== branchQuoteId;
      const statusNeeded = latest.status !== args.status;
      if (!relocateNeeded && !statusNeeded) return latest.id;
      const update = await api.updateBranchQuoteSupport({
        assignedSupplierUserId: null,
        notifySupplier: true,
        conversationId: null,

        quoteItemLink: null,
        branchQuoteSupportId: latest.id,
        quoteItemIdToDetach: null,
        status: statusNeeded ? args.status : null,
        branchQuoteId: relocateNeeded ? branchQuoteId : null,
        subject: null,
        newConversationEntries: null,
        editConversationEntries: null
      });
      if (update) {
        if (this.quoteId) this.ticketLists.delete(this.quoteId); // force reload
        this.publish([latest.id], SupportTicketReasons.TicketChanged);
        return latest.id;
      }
      return null;
    } catch (err) {
      this.trace('updateQuoteReviewTicket.error', err);
      throw err;
    }
  }

  async deleteTicket(ticketId: string): Promise<boolean> {
    const session = this.sessions.get(ticketId);
    if (!session) return true; // Nothing to delete
    // Non-persisted draft (never saved): just drop session
    if (session.kind === 'draft' && !session.ticket) {
      this.sessions.delete(ticketId);
      this.publish([ticketId], SupportTicketReasons.NewTicketWorkflow);
      return true;
    }
    // Persisted draft ticket
    const status = session.ticket?.status;
    const type = session.ticket?.type;
    // Enforce server-side rule client-side: only Draft standard tickets (not ETO, not QuoteReview) may be deleted
    if (
      status !== BranchQuoteSupportStatus.Draft ||
      type === BranchQuoteSupportItemType.EngineeredToOrder ||
      type === BranchQuoteSupportItemType.QuoteReview
    ) {
      return false; // Not eligible
    }
    try {
      const api = getApiFactory().franchisee();
      await api.deleteDraftBranchQuoteSupport({ branchQuoteSupportId: ticketId });
    } catch (err) {
      this.trace('deleteTicket.api.error', { ticketId, err });
      throw err;
    }
    // Remove from cached ticket list
    if (this.quoteId) {
      const list = this.ticketLists.get(this.quoteId);
      if (list) list.items = list.items.filter(t => t.id !== ticketId);
    }
    // Remove session
    this.sessions.delete(ticketId);
    // Publish list change so UI updates
    this.publish([ticketId], SupportTicketReasons.TicketListChanged);
    return true;
  }

  // ---- Internal consolidated special item status transition helper ----
  private async _performSpecialItemStatusTransition(args: {
    mode: 'cancel' | 'reactivate';
    ticketId: string;
    noteText: string;
  }): Promise<boolean> {
    const { mode, ticketId, noteText } = args;
    if (!ticketId) throw new Error(`_performSpecialItemStatusTransition(${mode}): ticketId required`);
    // Lightweight list hydration (avoids conversation load)
    if (!this.getTicketList()) {
      await this.loadTicketList({ force: false });
    }
    const list = this.getTicketList();
    const ticketObj = list?.items.find(t => t.id === ticketId) || null;
    if (!ticketObj) throw new Error(`specialItemTransition.${mode}: ticket not found in list cache`);
    try {
      this.ensureTicketHydrated(ticketObj); // hydrate minimal snapshot
    } catch (err) {
      this.trace(`specialItemTransition.ensureHydrated.error.${mode}`, err);
      throw err;
    }
    const session = this.getSession(ticketId);
    const currentStatus = session.ticket?.status ?? null;
    if (!currentStatus) throw new Error(`specialItemTransition.${mode}: ticket status unavailable`);
    if (mode === 'cancel') {
      if (currentStatus === BranchQuoteSupportStatus.Cancelled) return false; // no-op
    } else if (mode === 'reactivate') {
      if (containsFlag(currentStatus, BranchQuoteSupportStatus.ReActivated, BranchQuoteSupportStatus.Draft))
        return false; // no-op
    }
    // Stage status-change note entry
    this.addPendingConversationEntry({
      branchQuoteSupportId: ticketId,
      conversationEntryId: newGuid(),
      text: noteText,
      attachments: [],
      users: [],
      draft: false
    });
    const targetStatus = mode === 'cancel' ? BranchQuoteSupportStatus.Cancelled : BranchQuoteSupportStatus.ReActivated;
    try {
      const ok = await this.saveTicket(ticketId, {
        finalizeDraftEntry: true,
        status: targetStatus,
        combinedPublish: false
      });
      return ok;
    } catch (err) {
      this.trace(`specialItemTransition.save.error.${mode}`, err);
      throw err;
    }
  }
}

// (Legacy new ticket draft types removed in session-centric refactor)

// Factory for creating a new instance (quote manager will own lifecycle)
export function createSupportTicketService(): SupportTicketService {
  return new SupportTicketServiceImpl(); // base instance (not a clone)
}

/**
 * Example: Using clones for bulk, non-interfering edits
 *
 * const service = createSupportTicketService();
 * service.setQuoteContext(quoteSetId, quoteId);
 * // UI begins editing a ticket via service...
 *
 * // Later: perform batch conversation link updates without disturbing current editing slot
 * const worker = service.clone(); // shares list + events (default)
 * try {
 *   const list = worker.getTicketList();
 *   for (const t of list?.items || []) {
 *     await worker.openTicket(t.id, { edit: true, forceConversationReload: false });
 *     // stage changes (e.g., worker.addPendingConversationEntry({...}))
 *     await worker.saveAllChanges(t.supportType as BranchQuoteSupportItemType, { finalizedDraftEntry: true });
 *   }
 * } finally {
 *   worker.disposeClone?.(); // clean up isolated sessions
 * }
 *
 * Passing { shareEvents: false } to clone() suppresses publish to existing listeners during staging.
 */

// ---- Ticket List Cache Helpers & Public API (Phase: TicketListMigration) ----
// (Removed InternalTicketListFetchArgs – superseded by inline argument typing on loadTicketList)

// Extend prototype after class declaration for clarity of separation (could also be inside class above)
// but kept nearby to reduce merge complexity in ongoing refactor.
// Adding methods directly to class instead (cleaner) – placed here for patch locality.

// ---- Types for newly added methods (will refine as editor consolidation proceeds) ----
export interface AddEntryArgs {
  conversationEntryId: string;
  branchQuoteSupportId: string | null;
  text: string;
  draft?: boolean;
  attachments?: AddConversationAttachment[];
  users?: string[];
}

export interface UpdateStatusArgs {
  branchQuoteSupportId: string;
  status: BranchQuoteSupportStatus;
  previousStatus?: BranchQuoteSupportStatus; // to classify diff; optional when not known
}

export interface UpdateMetadataArgs {
  branchQuoteSupportId: string;
  branchQuoteId?: string;
  subject?: string;
  masterDocumentConversationId?: string;
  quoteItemLink?: { [key: string]: SupportQuoteItemLinkDetail } | null;
  detachQuoteItemId?: string;
}

export interface EnsureMasterDocumentArgs {
  branchQuoteSupportId: string;
  branchQuoteId?: string;
  currentMasterDocumentConversationId?: string | null;
  newConversationEntryId: string; // pre-generated for consistency with UI state
}

export interface ApplyFrameDiffArgs {
  branchQuoteSupportId: string;
  branchQuoteId?: string;
  markdownDiff?: string | null; // pre-rendered markdown
  subjectCandidate?: string;
  conversationEntryIdFactory: () => string; // supply for deterministic testability
}

export interface UpdateConversationEntryArgs {
  ticketId: string;
  entryId: string;
  text: string;
  draft: boolean;
  deleteEntry?: boolean;
  attachments: AddConversationAttachment[]; // TODO: unify type with update contract if server differs
  users?: string[];
}

export interface AttachSseOptions {
  quoteId: string; // branchQuoteId scope for filtering
}

export type GlobalInboxItem = ViewBranchQuoteSupportInboxItem;

// ---- Immutable State / Subscription Types ----
export interface SupportTicketSnapshot {
  id: string;
  ticket: ResultBranchQuoteSupport['branchQuoteSupport'] | null;
  masterDocumentConversationId: string | null;
  status: BranchQuoteSupportStatus | null;
  recordVersion: string | null;
  conversationLoaded: boolean;
  entries: ViewConversationEntry[]; // immutable copy
  draftMetadata: {
    subject: string | null;
    quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
    masterDocumentFilename: string | null;
    touched: { subject?: boolean; links?: boolean; masterDoc?: boolean };
    conflict: { subject?: boolean; links?: boolean; masterDoc?: boolean };
  } | null;
  hasMetadataChanges: boolean;
}

export interface SupportTicketsState {
  version: number;
  draftTicketIds: string[]; // tickets currently in draft creation state
  tickets: Record<string, SupportTicketSnapshot>;
  ticketLists: Record<
    string,
    {
      items: BranchQuoteSupport[];
      masterDocuments: ViewConversationEntry[];
      conversationLinks: any[];
    }
  >;
}

export interface SupportTicketsDelta {
  version: number;
  changedTicketIds: string[]; // empty means global or non-ticket change
  reason?: SupportTicketAnyReason; // optional semantic reason
}

export type SupportStateListener = (state: SupportTicketsState, delta: SupportTicketsDelta) => void;
